import { all, call, delay, fork, put, select, take } from 'redux-saga/effects'
import {
  databaseForEventsIsEmpty,
  dataForEventsIsAccepted,
  dataForEventsIsRejected,
} from '../../actions/bookingCalendar/event/requestDataForEvents'
import { callFetch } from '../../actions/fetch'
import { fireToastWarning } from '../../actions/toasts/fireToast'
import { database } from '../../database'
import i18n from '../../i18n'
import { fetchEvents } from '../../models/Event'
import mapEventFromDb from '../../models/Event/mappers/mapEventFromDb'
import { IStore } from '../../reducers'
import { EventActions } from '../../reducers/bookingCalendar/actionNames'
import { getCalendarId } from '../../reducers/bookingCalendar/selectors/calendar'
import { SynchronizeActions } from '../../reducers/synchronize/actionNames'
import { isUserAuthorized } from '../../reducers/userAccount/selectors/credentials'
import fetchActionsName, { ActionNames } from '../../utils/FetchActionsName'
import takeLatestWithSignOff from '../../utils/Saga/TakeLatestUntilSignoff'
import { loadFromApi } from './loadEvents'
import { getUserId, getWorkerId } from '../../reducers/userAccount/selectors/userAccount'

function* apiFallback(startDate, finishDate) {
  try {
    const actionNames = fetchActionsName(ActionNames.LoadEvents)

    const [{ calendarId, service }] = yield all([
      take(actionNames.output),
      put(callFetch(actionNames.input, actionNames.output, fetchEvents)),
    ])

    const events = yield call(
      service,
      calendarId,
      null,
      startDate,
      finishDate,
      true,
    )

    yield fork(loadFromApi, {
      onlyToBase: true,
      type: EventActions.DatabaseForEventsIsEmpty,
    })

    return events
  } catch (e) {
    yield put(
      dataForEventsIsRejected(
        e.message + `/n s:${startDate} f:${finishDate} /n` + e.stack,
      ),
    )
  }
}

function* load(action) {
  try {
    const fallbackLoading = yield select(
      (state: IStore) => state.events.request.events.isFallbackLoading,
    )
    const calendarId = yield select(getCalendarId)
    const isUserAuth = yield select(isUserAuthorized)

    if (undefined === calendarId || fallbackLoading) {
      yield delay(300)
      if (isUserAuth) {
        yield put(action)
      }
      return
    }

    const { startDate, finishDate } = action
    const userId = yield select(getUserId)
    const workerId = yield select(getWorkerId)

    const eventsInDB = yield database.events.where({ calendarId, userId: workerId || userId }).toArray()

    if (eventsInDB.length > 0) {
      const events = eventsInDB.map(mapEventFromDb)

      yield put(dataForEventsIsAccepted(events, false, true))
    } else {
      if (eventsInDB.length === 0) {
        const events = yield* apiFallback(startDate, finishDate)

        yield put(
          dataForEventsIsAccepted(events, false, false, startDate, finishDate),
        )

        yield put({
          lastModifyDate: new Date(),
          type: SynchronizeActions.SetSyncDate,
        })
      } else {
        yield put(dataForEventsIsAccepted([], false, true))
      }
    }
  } catch (e) {
    yield database.events.clear()
    yield put(databaseForEventsIsEmpty())
    yield fireToastWarning(i18n.t('error_server_response_4'))
  }
}

export default function*() {
  yield takeLatestWithSignOff(EventActions.RequestEventsForCalendar, load)
}
